import { Component, EventEmitter, Input, Output, OnDestroy, OnChanges, type SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import dayjs from 'dayjs';

export interface ExistingFile {
    _id: string;
    fileName: string;
    isFirstOneSelected?: boolean;
    isSecondOneSelected?: boolean;
    createdAt: string;
    fileSize: number;
    fileDownloadLink: string;
    updatedBy: string;
}

@Component({
    selector: 'app-upload-card',
    templateUrl: './upload-card.component.html',
    styleUrls: ['./upload-card.component.scss'],
    standalone: true,
    imports: [CommonModule, MatIconModule, MatButtonModule, MatInputModule, FormsModule]
})
export class UploadCardComponent implements OnChanges, OnDestroy {
    @Input() title: string = '';
    @Input() subtitle: string = 'Ainda não há arquivo';
    @Input() buttonText: string = 'ENVIAR ARQUIVO';
    @Input() acceptedFileTypes: string = '*/*';
    @Input() isFileNameEditable: boolean = false;
    @Input() existingFile: ExistingFile | null = null;
    @Input() primaryType: boolean = true;
    @Input() secondType: boolean = false;
    @Input() tertiaryType: boolean = false;

    @Output() fileUpload = new EventEmitter<File>();
    @Output() fileDelete = new EventEmitter<{ id: string }>();
    @Output() fileNameChange = new EventEmitter<{ name: string, id: string }>();

    private _uploadedFile: File | null = null;
    private _fileName: string = '';
    private _uploadDate: string = '';
    private fileNameChangeSubject = new Subject<{ name: string, id: string }>();
    private destroy$ = new Subject<void>();

    constructor() {
        this.fileNameChangeSubject
            .pipe(
                debounceTime(1000), // Aguarda 1s após a última digitação
                takeUntil(this.destroy$)
            )
            .subscribe((res: { name: string, id: string }) => {
                if (res.name === '' || res.name === null || res.name === undefined) return;
                this.fileNameChange.emit({ name: res.name, id: res.id });
            });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (this.secondType === true || this.tertiaryType === true) {
            this.primaryType = false;
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    get uploadedFile(): File | null {
        return this._uploadedFile;
    }

    get fileName(): string {
        if (this.existingFile) {
            return this.existingFile.fileName.split('.')[0];
        }
        return this._fileName;
    }

    get uploadDate(): string {
        if (this.existingFile) {
            return this.existingFile.createdAt;
        }
        return this._uploadDate;
    }

    get hasFile(): boolean {
        return this.existingFile !== null || this._uploadedFile !== null;
    }

    get uploadBy(): string {
        if (this.existingFile) {
            return this.existingFile.updatedBy;
        }

        if (this._uploadedFile) {
            const userName = localStorage.getItem('name');
            return userName || '';
        }

        return 'Usuário';
    }

    get displaySubtitle(): string {
        if (this.hasFile) {
            if (this.existingFile) {
                const formattedDate = dayjs(this.existingFile.createdAt).format('DD/MM/YYYY HH:mm');
                return `Enviado ${formattedDate} por ${this.uploadBy}`;
            } else if (this._uploadedFile) {
                return `Enviado ${this._uploadDate} por ${this.uploadBy}`;
            }
        }
        return this.subtitle;
    }

    onFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        if (input.files && input.files[0]) {
            const file = input.files[0];
            this._uploadedFile = file;
            this._fileName = file.name;
            this._uploadDate = this.formatDate(new Date());
            this.fileUpload.emit(file);
            input.value = '';
        }
    }

    onDeleteFile(): void {
        if (this.existingFile) this.fileDelete.emit({ id: this.existingFile._id });
        this._uploadedFile = null;
        this._fileName = '';
        this._uploadDate = '';
    }

    onFileNameChange(newName: string): void {
        this._fileName = newName;
        this.fileNameChangeSubject.next({ name: newName, id: this.existingFile?._id });
    }

    private formatDate(date: Date): string {
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    }
}
